import {
  Checkbox,
  HStack,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import Select from "../../../../../shared/components/Select";
import useApi from "../../../../../shared/hooks/useApi";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import EntityProfileActionBar from "../../../../entityProfileModal/components/EntityProfileActionBar";
import EntityProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import {
  EntityProfileFieldSection,
  EntityProfileInlineControlRow,
  EntityProfileSplitFieldRow,
} from "../../../../entityProfileModal/components/EntityProfileFieldLayout";
import EntityProfileInlineEditableRow from "../../../../entityProfileModal/components/EntityProfileInlineEditableRow";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";

type Props = {
  caregiverId: CaregiverId;
};

type PayrollTax = {
  documentType?: "UNTIL_2020" | "2020_ONWARDS" | null;
  federalMaritalStatus?: string | null;
  federalWithholdingExemption?: number | null;
  federalExtraWithholdingDollar?: number | null;
  federalWithholdingExemptionConditions?: boolean | null;
  multipleJobs?: boolean | null;
  federalDependentsClaimsTotalDollar?: number | null;
  federalOtherIncomeDollar?: number | null;
  federalDeductionsDollar?: number | null;
  stateMaritalStatus?: string | null;
  cityTaxCode?: number | string | null;
  cityTaxCodeName?: string | null;
  stateDependents?: number | null;
  localDependents?: number | null;
  stateExtraWithholdingDollar?: number | null;
  nycExtraWithholdingDollar?: number | null;
  yonkersExtraWithholdingDollar?: number | null;
};

type PayrollTaxKey = keyof PayrollTax;

const documentTypes = [
  { value: "UNTIL_2020" as const, label: "Until 2020" },
  { value: "2020_ONWARDS" as const, label: "2020 Onwards" },
];

const federalUntil2020MaritalStatuses = [
  "Single",
  "Married",
  "Married, but withhold at higher Single rate",
];

const federal2020OnwardsMaritalStatuses = [
  "Single or Married filing separately",
  "Married filing jointly or Qualifying widow(er)",
  "Head of household",
];

const stateMaritalStatuses = [
  "Single or Head of household",
  "Married",
  "Married, but withhold at higher single rate",
];

export default function CaregiverProfilePayrollTaxSection(props: Props) {
  const { api } = useApi();
  const canEdit = auth.isAllowedTo("edit_caregiver_page_payroll_tax");
  const toast = useToast({ position: "top-right" });
  const queryClient = useQueryClient();
  const queryKey = ["caregiverProfileModal", props.caregiverId, "payrollTax"];
  const payrollTaxQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.anyRequest<PayrollTax>(
        "GET",
        "agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/payroll_tax",
        { path: { caregiverId: props.caregiverId } }
      ),
  });
  const payrollTax = payrollTaxQuery.data ?? {};
  const [isEditing, setIsInEditMode] = React.useState(false);
  const isInEditMode = canEdit && isEditing;
  const [activeField, setActiveField] = React.useState<PayrollTaxKey | null>(null);
  React.useEffect(() => {
    if (!isInEditMode) {
      setActiveField(null);
    }
  }, [isInEditMode]);
  const updateMutation = useMutation({
    mutationFn: (body: Partial<PayrollTax>) =>
      api.anyRequest<PayrollTax>(
        "PUT",
        "agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/payroll_tax",
        { body, path: { caregiverId: props.caregiverId } }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast({ status: "success", title: "Caregiver payroll tax updated" });
    },
    onError: () => {
      toast({ status: "error", title: "Could not update caregiver payroll tax" });
    },
  });
  const saveField = (field: PayrollTaxKey, value: PayrollTax[PayrollTaxKey]) => {
    if (!isInEditMode) {
      return;
    }

    updateMutation.mutate({
      documentType: payrollTax.documentType ?? "2020_ONWARDS",
      [field]: value,
    });
    setActiveField(null);
  };
  const editableRowProps = (field: PayrollTaxKey) => ({
    activeField,
    emptyPlaceholder: isInEditMode,
    field,
    isEditable: isInEditMode && !updateMutation.isPending,
    onActivate: setActiveField,
    onCancel: () => setActiveField(null),
  });
  const federalMaritalStatusOptions = (
    payrollTax.documentType === "UNTIL_2020"
      ? federalUntil2020MaritalStatuses
      : federal2020OnwardsMaritalStatuses
  ).map((status) => ({ value: status, label: status }));
  const stateMaritalStatusOptions = stateMaritalStatuses.map((status) => ({
    value: status,
    label: status,
  }));

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load payroll tax information."
      isError={payrollTaxQuery.isError}
      isLoading={payrollTaxQuery.isLoading}
      loadingLabel="Loading payroll tax information"
      onRetry={() => void payrollTaxQuery.refetch()}
    >
      <VStack align="stretch" spacing={6}>
        {canEdit ? (
          <EntityProfileActionBar>
            <EntityProfileEditModeButton
              isDisabled={updateMutation.isPending}
              isEditing={isInEditMode}
              label="Edit"
              onClick={() => setIsInEditMode((current) => !current)}
            />
          </EntityProfileActionBar>
        ) : null}
        <PayrollTaxBlock title="Federal">
          <DocumentVersionRadioRow
            isDisabled={!isInEditMode || updateMutation.isPending}
            label="Document Version (W4)"
            options={documentTypes}
            value={payrollTax.documentType ?? null}
            onChange={(value) => saveField("documentType", value as PayrollTax["documentType"])}
          />

          {payrollTax.documentType === "UNTIL_2020" ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <EditableSelect
                label="Marital Status"
                options={federalMaritalStatusOptions}
                value={payrollTax.federalMaritalStatus ?? null}
                {...editableRowProps("federalMaritalStatus")}
                onChange={(value) => saveField("federalMaritalStatus", value ?? null)}
              />
              <EditableNumber
                label="Total Number Of Allowances"
                value={payrollTax.federalWithholdingExemption}
                {...editableRowProps("federalWithholdingExemption")}
                onSave={(value) => saveField("federalWithholdingExemption", value)}
              />
              <EditableNumber
                label="Additional Amount ($)"
                value={payrollTax.federalExtraWithholdingDollar}
                {...editableRowProps("federalExtraWithholdingDollar")}
                onSave={(value) => saveField("federalExtraWithholdingDollar", value)}
              />
              <EditableCheckbox
                label="Withholding Exemption Conditions"
                value={payrollTax.federalWithholdingExemptionConditions === true}
                {...editableRowProps("federalWithholdingExemptionConditions")}
                onChange={(value) => saveField("federalWithholdingExemptionConditions", value)}
              />
            </SimpleGrid>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <EditableSelect
                label="Marital Status"
                options={federalMaritalStatusOptions}
                value={payrollTax.federalMaritalStatus ?? null}
                {...editableRowProps("federalMaritalStatus")}
                onChange={(value) => saveField("federalMaritalStatus", value ?? null)}
              />
              <EditableCheckbox
                label="Multiple Jobs"
                value={payrollTax.multipleJobs === true}
                {...editableRowProps("multipleJobs")}
                onChange={(value) => saveField("multipleJobs", value)}
              />
              <EditableNumber
                label="Dependents Claims Total ($)"
                value={payrollTax.federalDependentsClaimsTotalDollar}
                {...editableRowProps("federalDependentsClaimsTotalDollar")}
                onSave={(value) => saveField("federalDependentsClaimsTotalDollar", value)}
              />
              <EditableNumber
                label="Other Income ($)"
                value={payrollTax.federalOtherIncomeDollar}
                {...editableRowProps("federalOtherIncomeDollar")}
                onSave={(value) => saveField("federalOtherIncomeDollar", value)}
              />
              <EditableNumber
                label="Deductions ($)"
                value={payrollTax.federalDeductionsDollar}
                {...editableRowProps("federalDeductionsDollar")}
                onSave={(value) => saveField("federalDeductionsDollar", value)}
              />
              <EditableNumber
                label="Extra Withholding ($)"
                value={payrollTax.federalExtraWithholdingDollar}
                {...editableRowProps("federalExtraWithholdingDollar")}
                onSave={(value) => saveField("federalExtraWithholdingDollar", value)}
              />
            </SimpleGrid>
          )}
        </PayrollTaxBlock>

        <PayrollTaxBlock title="State (NY)">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <EditableSelect
              label="Marital Status"
              options={stateMaritalStatusOptions}
              value={payrollTax.stateMaritalStatus ?? null}
              {...editableRowProps("stateMaritalStatus")}
              onChange={(value) => saveField("stateMaritalStatus", value ?? null)}
            />
            <ReadOnlyField
              label="City Tax Code"
              value={payrollTax.cityTaxCodeName ?? payrollTax.cityTaxCode}
            />
            <EditableNumber
              label="Total number of allowances (State + Yonkers)"
              value={payrollTax.stateDependents}
              {...editableRowProps("stateDependents")}
              onSave={(value) => saveField("stateDependents", value)}
            />
            <EditableNumber
              label="Total number of allowances (NYC)"
              value={payrollTax.localDependents}
              {...editableRowProps("localDependents")}
              onSave={(value) => saveField("localDependents", value)}
            />
            <EditableNumber
              label="New York State Amount ($)"
              value={payrollTax.stateExtraWithholdingDollar}
              {...editableRowProps("stateExtraWithholdingDollar")}
              onSave={(value) => saveField("stateExtraWithholdingDollar", value)}
            />
            <EditableNumber
              label="New York City Amount ($)"
              value={payrollTax.nycExtraWithholdingDollar}
              {...editableRowProps("nycExtraWithholdingDollar")}
              onSave={(value) => saveField("nycExtraWithholdingDollar", value)}
            />
            <EditableNumber
              label="Yonkers Amount ($)"
              value={payrollTax.yonkersExtraWithholdingDollar}
              {...editableRowProps("yonkersExtraWithholdingDollar")}
              onSave={(value) => saveField("yonkersExtraWithholdingDollar", value)}
            />
          </SimpleGrid>
        </PayrollTaxBlock>
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PayrollTaxBlock(props: { children: React.ReactNode; title: string }) {
  return (
    <EntityProfileFieldSection title={props.title}>{props.children}</EntityProfileFieldSection>
  );
}

function ReadOnlyField(props: { label: string; value: unknown }) {
  return <PayrollTaxDisplayRow label={props.label} value={formatValue(props.value)} />;
}

type EditableFieldProps = {
  activeField: PayrollTaxKey | null;
  emptyPlaceholder: boolean;
  field: PayrollTaxKey;
  isEditable: boolean;
  label: string;
  onActivate: (field: PayrollTaxKey) => void;
  onCancel: () => void;
};

function EditableNumber(
  props: EditableFieldProps & {
    value: number | null | undefined;
    onSave: (value: number | null) => void;
  }
) {
  const [value, setValue] = React.useState(formatValue(props.value));

  React.useEffect(() => setValue(formatValue(props.value)), [props.value]);

  return (
    <EntityProfileInlineEditableRow
      activeField={props.activeField}
      emptyPlaceholder={props.emptyPlaceholder}
      field={props.field}
      isEditable={props.isEditable}
      label={props.label}
      value={formatValue(props.value)}
      onActivate={props.onActivate}
      onCancel={() => {
        setValue(formatValue(props.value));
        props.onCancel();
      }}
      onCommit={() => props.onSave(value === "" ? null : Number(value))}
    >
      <Input
        maxW="220px"
        size="sm"
        type="number"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
    </EntityProfileInlineEditableRow>
  );
}

function EditableSelect(
  props: EditableFieldProps & {
    label: string;
    options: Array<{ label: string; value: string }>;
    value: string | null;
    onChange: (value: string | undefined) => void;
  }
) {
  const [value, setValue] = React.useState<string | null>(props.value);

  React.useEffect(() => setValue(props.value), [props.value]);

  return (
    <EntityProfileInlineEditableRow
      activeField={props.activeField}
      emptyPlaceholder={props.emptyPlaceholder}
      field={props.field}
      isEditable={props.isEditable}
      label={props.label}
      value={props.options.find((option) => option.value === props.value)?.label ?? props.value}
      onActivate={props.onActivate}
      onCancel={() => {
        setValue(props.value);
        props.onCancel();
      }}
      onCommit={() => props.onChange(value ?? undefined)}
    >
      <Select
        allowUnselect={false}
        label={`Select ${props.label}`}
        multiple={false}
        options={props.options}
        size="sm"
        value={value}
        width="320px"
        onChange={(nextValue) => setValue(nextValue ?? null)}
      />
    </EntityProfileInlineEditableRow>
  );
}

function EditableCheckbox(
  props: EditableFieldProps & {
    label: string;
    value: boolean;
    onChange: (value: boolean) => void;
  }
) {
  const [value, setValue] = React.useState(props.value);

  React.useEffect(() => setValue(props.value), [props.value]);

  return (
    <EntityProfileInlineEditableRow
      activeField={props.activeField}
      emptyPlaceholder={props.emptyPlaceholder}
      field={props.field}
      isEditable={props.isEditable}
      label={props.label}
      value={props.value ? "True" : "False"}
      onActivate={props.onActivate}
      onCancel={() => {
        setValue(props.value);
        props.onCancel();
      }}
      onCommit={() => props.onChange(value)}
    >
      <Checkbox isChecked={value} onChange={(event) => setValue(event.target.checked)}>
        Yes
      </Checkbox>
    </EntityProfileInlineEditableRow>
  );
}

function DocumentVersionRadioRow(props: {
  isDisabled: boolean;
  label: string;
  options: Array<{ label: string; value: string }>;
  value: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <EntityProfileInlineControlRow label={props.label}>
      <RadioGroup
        value={props.value ?? ""}
        onChange={(value) => {
          if (!props.isDisabled) {
            props.onChange(value);
          }
        }}
      >
        <HStack spacing={6}>
          {props.options.map((option) => (
            <Radio
              key={option.value}
              colorScheme="blue"
              isDisabled={props.isDisabled}
              value={option.value}
            >
              {option.label}
            </Radio>
          ))}
        </HStack>
      </RadioGroup>
    </EntityProfileInlineControlRow>
  );
}

function PayrollTaxDisplayRow(props: { label: string; value: React.ReactNode }) {
  return <EntityProfileSplitFieldRow label={props.label} value={props.value} />;
}

function formatValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
}
